import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {BZ_DIALOG_DATA, BzDialogRef} from '@bringoz/ui';
import {bzIconHistoryLog} from '@bringoz/ui/icons';
import * as _ from 'lodash';
import {
  EditAdminTimeChange,
  EditAdminTimeDialogData,
  EditAdminTimeDialogResult
} from '../../../types/timekeeping-dialog.type';
import {durationToMillis, splitDuration} from '../../utils/timekeeping-dialog.util';

type DurationField = 'hours' | 'minutes';

interface AdminTimeRow {
  timeCardId: string;
  shiftName: string;
  isLocked: boolean;
  initialAdminTime: number;
  // Strings while editing - an empty field stays empty until it loses focus
  hours: string;
  minutes: string;
}

/**
 * Edits Admin Time per shift of the day. Resolves only the shifts the user changed; locked shifts are
 * read-only.
 */
require('../timekeeping-dialogs.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-admin-time-dialog',
  templateUrl: './timecard-admin-time-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardAdminTimeDialogComponent {

  readonly data = inject<EditAdminTimeDialogData>(BZ_DIALOG_DATA);
  private readonly ref = inject<BzDialogRef<EditAdminTimeDialogResult>>(BzDialogRef);

  readonly icons = {history: bzIconHistoryLog};

  readonly rows = signal<AdminTimeRow[]>((this.data.shifts || []).map((shift) => {
    const {hours, minutes} = splitDuration(shift.adminTime);
    return {
      timeCardId: shift.timeCardId,
      shiftName: shift.shiftName,
      isLocked: shift.isLocked,
      initialAdminTime: shift.adminTime || 0,
      hours: _.toString(hours),
      minutes: _.toString(minutes)
    };
  }));

  readonly changes = computed<EditAdminTimeChange[]>(() => this.rows()
    .filter((row) => !row.isLocked && durationToMillis(row.hours, row.minutes) !== row.initialAdminTime)
    .map((row) => ({timeCardId: row.timeCardId, adminTime: durationToMillis(row.hours, row.minutes)})));

  /** Selects the value so typing replaces the 0 instead of appending to it. */
  onFocusIn(event: FocusEvent) {
    const target = event.target as HTMLInputElement;
    target?.select?.();
  }

  onValueChange(index: number, field: DurationField, value: string) {
    this.updateRow(index, field, value);
  }

  /** Empty or invalid becomes 0; minutes are capped at 59. */
  onFocusOut(index: number, field: DurationField) {
    const raw = this.rows()[index][field];
    const value = Math.max(0, Math.floor(Number(raw)));
    const normalized = raw === '' || !Number.isFinite(value) ? 0 : field === 'minutes' ? Math.min(59, value) : value;
    this.updateRow(index, field, _.toString(normalized));
  }

  onOpenHistory(row: AdminTimeRow) {
    this.data.onOpenHistory?.(row.timeCardId);
  }

  onCancel() {
    this.ref.close();
  }

  onSubmit() {
    if (this.changes().length) {
      this.ref.close({changes: this.changes()});
    }
  }

  private updateRow(index: number, field: DurationField, value: string) {
    this.rows.update((rows) => rows.map((row, rowIndex) => rowIndex === index ? {...row, [field]: value} : row));
  }
}
